import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  computed,
  effect,
  signal
} from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { PaddockDexie } from '../../../../models/paddock.model';
import { PropertyDexie } from '../../../../models/property.model';
import { startWith, Subscription } from 'rxjs';

// Custom validator for at least one selection
const atLeastOneSelected: ValidatorFn = (
  formGroup: AbstractControl
): ValidationErrors | null => {
  if (!(formGroup instanceof FormGroup)) {
    return null;
  }

  const propertySelected = formGroup.get('property')?.value || false;
  const paddocksSelected =
    formGroup.get('paddocks')?.value?.some((selected: boolean) => selected) ||
    false;

  return propertySelected || paddocksSelected
    ? null
    : { atLeastOneSelected: true };
};

@Component({
  selector: 'app-report-card',
  templateUrl: './report-card.component.html',
  styleUrls: ['./report-card.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatRadioModule,
    MatDividerModule,
    MatExpansionModule
  ]
})
export class ReportCardComponent implements OnInit, OnChanges, OnDestroy {
  private readonly cdRef = inject(ChangeDetectorRef);

  // Input properties using modern Angular signals
  @Input() property!: PropertyDexie;
  @Input() paddocks: PaddockDexie[] = [];
  @Input() multi = false;

  // Output event
  @Output() runReport = new EventEmitter<
    PropertyDexie | PaddockDexie | (PropertyDexie | PaddockDexie)[]
  >();

  // Form properties
  form!: FormGroup;
  selectedTargetControl!: FormControl;

  private formValue = signal<any>(null);
  private formValueSubscription?: Subscription;

  // Computed properties using signals
  readonly selectedPaddocks = computed(() => {
    if (!this.form || !this.multi) return [];

    const paddocksArray = this.form.get('paddocks') as FormArray;
    if (!paddocksArray) return [];

    return this.paddocks
      .filter((_, index) => paddocksArray.at(index)?.value)
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  readonly reportingTargets = computed(() => {
    const value = this.formValue();

    if (!this.form || value === null) return [];

    if (this.multi) {
      const propertySelected = value.property || false;
      const propertyTargets = propertySelected ? [this.property] : [];
      return [...propertyTargets, ...this.selectedPaddocks()];
    } else {
      return value.selectedTarget || null;
    }
  });

  // Effect to handle form changes
  private readonly formEffect = effect(() => {
    if (this.form && this.multi) {
      console.log('Selected paddocks:', this.selectedPaddocks());
    }
  });

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['multi'] || changes['property'] || changes['paddocks']) {
      this.initializeForm();
    }
  }

  ngOnDestroy(): void {
    if (this.form && this.form.contains('selectedTarget')) {
      this.form.removeControl('selectedTarget');
    }
  }

  private initializeForm(): void {
    if (this.multi) {
      this.createMultiSelectForm();
    } else {
      this.createSingleSelectForm();
    }
  }

  private createSingleSelectForm(): void {
    this.selectedTargetControl = new FormControl<PropertyDexie | PaddockDexie>(
      this.property,
      [Validators.required]
    );

    this.form = new FormGroup(
      {
        selectedTarget: this.selectedTargetControl
      },
      [Validators.required]
    );
    this.formValueSubscription?.unsubscribe();
    this.formValueSubscription = this.form.valueChanges
      .pipe(startWith(this.form.value))
      .subscribe((value) => this.formValue.set(value));
  }

  private createMultiSelectForm(): void {
    const paddockFormControls = this.paddocks.map(() => new FormControl(true));

    this.form = new FormGroup(
      {
        property: new FormControl<boolean>(true),
        paddocks: new FormArray(paddockFormControls)
      },
      [atLeastOneSelected]
    );

    // Subscribe to form changes for debugging
    this.form.valueChanges.subscribe((value) => {
      console.log('Form value changed:', value);
    });

    this.formValueSubscription?.unsubscribe();
    this.formValueSubscription = this.form.valueChanges
      .pipe(startWith(this.form.value))
      .subscribe((value) => this.formValue.set(value));
  }

  /**
   * Gets the currently selected paddocks in sorted order
   * @returns An array of paddocks that have been selected by the user
   */
  getSelectedPaddocks(): PaddockDexie[] {
    return this.selectedPaddocks();
  }

  /**
   * Gets the current reporting targets based on form state
   * @returns The selected property, paddock, or array of both
   */
  getReportingTargets():
    | PropertyDexie
    | PaddockDexie
    | (PropertyDexie | PaddockDexie)[] {
    return this.reportingTargets();
  }

  /**
   * Handles the run report button click
   */
  onClick(): void {
    if (this.form.valid) {
      this.runReport.emit(this.getReportingTargets());
    } else {
      console.warn('Form is invalid:', this.form.errors);
    }
  }

  /**
   * Checks if the form is valid
   * @returns True if the form is valid, false otherwise
   */
  isFormValid(): boolean {
    return this.form?.valid || false;
  }

  /**
   * Gets form validation errors
   * @returns Object containing validation errors
   */
  getFormErrors(): ValidationErrors | null {
    return this.form?.errors || null;
  }
}
